import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  PartyPopper, CreditCard, FileSignature, UserCircle, Upload, Check, X,
  ChevronRight, ChevronLeft, Loader2, Compass,
} from 'lucide-react';
import api from '../utils/api';

// First-login wizard for a new internal agent: warm welcome, collect personal/HR
// data, upload DNI, e-sign the onboarding documents, then offer the guided tour.
// Skippable ("más tarde") but returns each login until onboarding is complete.
const STEPS = ['welcome', 'data', 'dni', 'docs', 'finish'];

const authUrl = (url) => {
  if (!url) return url;
  const tok = localStorage.getItem('accessToken');
  return tok ? `${url}${url.includes('?') ? '&' : '?'}token=${tok}` : url;
};

export default function AgentOnboardingModal({ onClose, onStartTour }) {
  const { t } = useTranslation();
  const [stepIdx, setStepIdx] = useState(0);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({});
  const [docs, setDocs] = useState([]);
  const [openDoc, setOpenDoc] = useState(null);
  const [signName, setSignName] = useState('');
  const [saving, setSaving] = useState(false);
  const frontRef = useRef();
  const backRef = useRef();

  const step = STEPS[stepIdx];

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/agent-onboarding/status');
        setStatus(r.data);
        const p = r.data.profile || {};
        setForm({
          dni: p.dni || '', birthDate: (p.birth_date || '').split('T')[0], address: p.address || '',
          phone: p.phone || '', whatsapp: p.whatsapp || '', emergencyContact: p.emergency_contact || '',
          emergencyPhone: p.emergency_phone || '', cuil: p.cuil || '', cbu: p.cbu || '',
          aliasCbu: p.alias_cbu || '', bankName: p.bank_name || '', taxCondition: p.tax_condition || '',
        });
        setDocs(r.data.documents || []);
        setSignName(`${r.data.user?.firstName || ''} ${r.data.user?.lastName || ''}`.trim());
        api.post('/agent-onboarding/welcome/seen').catch(() => {});
      } catch { /* non-blocking */ }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveData = async () => {
    setSaving(true);
    try {
      await api.patch('/agent-onboarding/me/profile', form);
      toast.success(t('common.saved'));
      setStepIdx((i) => i + 1);
    } catch {
      toast.error(t('common.actionFailed'));
    } finally { setSaving(false); }
  };

  const uploadDni = async (file, side) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('photo', file);
    try {
      const r = await api.post(`/agent-onboarding/me/dni-photo?side=${side}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStatus((s) => ({ ...s, profile: { ...s.profile, [side === 'back' ? 'dni_photo_back_url' : 'dni_photo_url']: r.data.url } }));
      toast.success(t('agentOnboarding.dniUploaded'));
    } catch {
      toast.error(t('common.actionFailed'));
    }
  };

  const signDoc = async (doc) => {
    if (!signName.trim()) { toast.error(t('agentOnboarding.typeName')); return; }
    try {
      await api.post(`/agent-onboarding/documents/${doc.id}/sign`, { fullName: signName.trim() });
      setDocs((ds) => ds.map((d) => (d.id === doc.id ? { ...d, signed: true } : d)));
      setOpenDoc(null);
      toast.success(t('agentOnboarding.signed'));
    } catch {
      toast.error(t('common.actionFailed'));
    }
  };

  const finish = async (startTour) => {
    try { await api.post('/agent-onboarding/complete'); } catch { /* best-effort */ }
    onClose?.();
    if (startTour) onStartTour?.();
  };

  const profile = status?.profile || {};
  const requiredUnsigned = docs.filter((d) => d.required_for_onboarding && !d.signed);
  const canFinishDocs = requiredUnsigned.length === 0;

  const next = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const prev = () => setStepIdx((i) => Math.max(i - 1, 0));

  const Field = ({ label, k, type = 'text', placeholder }) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input className="form-input" type={type} value={form[k] || ''} placeholder={placeholder || ''}
        onChange={(e) => set(k, e.target.value)} />
    </div>
  );

  const modal = (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000, display: 'flex',
      alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)', padding: 16,
    }}>
      <div className="card" style={{ margin: 0, maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* header / progress */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 4, flex: 1 }}>
            {STEPS.map((s, i) => (
              <div key={s} style={{ height: 4, flex: 1, borderRadius: 2, background: i <= stepIdx ? 'var(--c-brand)' : 'var(--c-border)' }} />
            ))}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => onClose?.()}>{t('agentOnboarding.later')}</button>
        </div>

        {/* WELCOME */}
        {step === 'welcome' && (
          <div style={{ textAlign: 'center', padding: '12px 4px' }}>
            <PartyPopper size={40} style={{ color: 'var(--c-brand)' }} />
            <h2 style={{ margin: '12px 0 6px' }}>{t('agentOnboarding.welcomeTitle', { name: status?.user?.firstName || '' })}</h2>
            <p style={{ color: 'var(--c-text-muted)', lineHeight: 1.6, maxWidth: 460, margin: '0 auto 16px' }}>
              {t('agentOnboarding.welcomeBody')}
            </p>
            <button className="btn btn-primary" onClick={next}>{t('agentOnboarding.start')} <ChevronRight size={16} /></button>
          </div>
        )}

        {/* DATA */}
        {step === 'data' && (
          <div>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}><UserCircle size={20} /> {t('agentOnboarding.dataTitle')}</h3>
            <p style={{ color: 'var(--c-text-muted)', fontSize: 13, marginTop: 0 }}>{t('agentOnboarding.dataIntro')}</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {Field({ label: t('agentOnboarding.f.dni'), k: 'dni' })}
              {Field({ label: t('agentOnboarding.f.birthDate'), k: 'birthDate', type: 'date' })}
              <div style={{ gridColumn: '1 / -1' }}>{Field({ label: t('agentOnboarding.f.address'), k: 'address' })}</div>
              {Field({ label: t('agentOnboarding.f.phone'), k: 'phone' })}
              {Field({ label: t('agentOnboarding.f.whatsapp'), k: 'whatsapp' })}
              {Field({ label: t('agentOnboarding.f.emergencyContact'), k: 'emergencyContact' })}
              {Field({ label: t('agentOnboarding.f.emergencyPhone'), k: 'emergencyPhone' })}
              {Field({ label: t('agentOnboarding.f.cuil'), k: 'cuil' })}
              {Field({ label: t('agentOnboarding.f.taxCondition'), k: 'taxCondition' })}
              {Field({ label: t('agentOnboarding.f.cbu'), k: 'cbu' })}
              {Field({ label: t('agentOnboarding.f.aliasCbu'), k: 'aliasCbu' })}
              {Field({ label: t('agentOnboarding.f.bankName'), k: 'bankName' })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={prev}><ChevronLeft size={16} /> {t('common.back')}</button>
              <button className="btn btn-primary" onClick={saveData} disabled={saving}>
                {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <>{t('common.saveAndContinue')} <ChevronRight size={16} /></>}
              </button>
            </div>
          </div>
        )}

        {/* DNI */}
        {step === 'dni' && (
          <div>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}><CreditCard size={20} /> {t('agentOnboarding.dniTitle')}</h3>
            <p style={{ color: 'var(--c-text-muted)', fontSize: 13, marginTop: 0 }}>{t('agentOnboarding.dniIntro')}</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[['front', profile.dni_photo_url, frontRef], ['back', profile.dni_photo_back_url, backRef]].map(([side, url, ref]) => (
                <div key={side} style={{ border: '1px dashed var(--c-border)', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{t(`agentOnboarding.dni.${side}`)}</div>
                  {url ? (
                    <a href={authUrl(url)} target="_blank" rel="noreferrer">
                      <img src={authUrl(url)} alt={side} style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 6 }} />
                    </a>
                  ) : (
                    <div style={{ color: 'var(--c-text-muted)', fontSize: 12, padding: 20 }}>{t('agentOnboarding.noPhoto')}</div>
                  )}
                  <input ref={ref} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                    onChange={(e) => uploadDni(e.target.files[0], side)} />
                  <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={() => ref.current?.click()}>
                    {url ? <><Check size={13} /> {t('agentOnboarding.replace')}</> : <><Upload size={13} /> {t('agentOnboarding.upload')}</>}
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={prev}><ChevronLeft size={16} /> {t('common.back')}</button>
              <button className="btn btn-primary" onClick={next}>{t('common.continue')} <ChevronRight size={16} /></button>
            </div>
          </div>
        )}

        {/* DOCS */}
        {step === 'docs' && (
          <div>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}><FileSignature size={20} /> {t('agentOnboarding.docsTitle')}</h3>
            <p style={{ color: 'var(--c-text-muted)', fontSize: 13, marginTop: 0 }}>{t('agentOnboarding.docsIntro')}</p>
            {docs.map((doc) => (
              <div key={doc.id} style={{ border: '1px solid var(--c-border)', borderRadius: 8, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10, cursor: 'pointer' }}
                  onClick={() => setOpenDoc(openDoc === doc.id ? null : doc.id)}>
                  {doc.signed ? <Check size={16} style={{ color: '#059669' }} /> : <FileSignature size={16} style={{ color: 'var(--c-text-muted)' }} />}
                  <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{doc.title}</span>
                  {doc.signed
                    ? <span className="badge badge-resolved">{t('agentOnboarding.signedBadge')}</span>
                    : <span className="badge badge-pending">{t('agentOnboarding.pendingSign')}</span>}
                </div>
                {openDoc === doc.id && (
                  <div style={{ padding: '0 12px 12px' }}>
                    <div style={{ maxHeight: 220, overflowY: 'auto', fontSize: 13, lineHeight: 1.6, border: '1px solid var(--c-border)', borderRadius: 6, padding: 12, background: 'var(--c-surface-2)' }}
                      dangerouslySetInnerHTML={{ __html: doc.body_html }} />
                    {!doc.signed && (
                      <div style={{ marginTop: 10 }}>
                        <label className="form-label">{t('agentOnboarding.signLabel')}</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input className="form-input" value={signName} onChange={(e) => setSignName(e.target.value)} placeholder={t('agentOnboarding.fullNamePlaceholder')} />
                          <button className="btn btn-primary" onClick={() => signDoc(doc)}><FileSignature size={14} /> {t('agentOnboarding.signBtn')}</button>
                        </div>
                        <p style={{ fontSize: 11, color: 'var(--c-text-muted)', marginTop: 6 }}>{t('agentOnboarding.signDisclaimer')}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={prev}><ChevronLeft size={16} /> {t('common.back')}</button>
              <button className="btn btn-primary" onClick={next} disabled={!canFinishDocs}
                title={!canFinishDocs ? t('agentOnboarding.mustSign') : ''}>
                {t('common.continue')} <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* FINISH */}
        {step === 'finish' && (
          <div style={{ textAlign: 'center', padding: '12px 4px' }}>
            <Check size={40} style={{ color: '#059669' }} />
            <h2 style={{ margin: '12px 0 6px' }}>{t('agentOnboarding.finishTitle')}</h2>
            <p style={{ color: 'var(--c-text-muted)', lineHeight: 1.6, maxWidth: 460, margin: '0 auto 16px' }}>
              {t('agentOnboarding.finishBody')}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={() => finish(false)}>{t('agentOnboarding.finishLater')}</button>
              <button className="btn btn-primary" onClick={() => finish(true)}><Compass size={16} /> {t('agentOnboarding.startTour')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
